import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldCheck, TriangleAlert } from "lucide-react";
import { api } from "../lib/api";
import { fmtAxv, IMAGES } from "../lib/tokens";
import Plate from "../components/Plate";

const KIND_LABEL = {
  mint: "Genesis grant",
  ante: "Self-ante (PlayForward)",
  spend: "MelzAI clustering (Stageward)",
  topup: "Purchase (Stripe — mocked)",
  release: "Stake released back to Pouch",
};

export default function LedgerPage() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [recon, setRecon] = useState(null);

  useEffect(() => {
    api.get("/ledger/history").then((r) => setData(r.data));
  }, []);

  const replay = async () => {
    setRecon("loading");
    const { data: r } = await api.get("/ledger/reconcile");
    setRecon(r);
  };

  return (
    <div className="relative min-h-screen overflow-hidden">
      <Plate src={IMAGES.atlasIslands} depth={7} />

      <div className="relative z-10 mx-auto max-w-4xl px-6 pb-28 pt-20 md:px-10">
        <button
          type="button"
          data-testid="ledger-back"
          onClick={() => nav(-1)}
          className="tracked mb-12 flex items-center gap-2 text-[9px] text-white/40 transition-colors duration-200 hover:text-[var(--stage-gold)]"
        >
          <ArrowLeft size={12} /> Back
        </button>

        <motion.div initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }} className="slab-cool p-7 md:p-9">
          <div className="tracked mb-5 text-[10px] text-[var(--atlas-gold)]/70">
            The Ledger · append-only
          </div>
          <h1 className="serif text-4xl font-light leading-tight text-[#f4ecdd] sm:text-5xl">
            Balances are never stored.
            <br />
            <span className="italic text-[var(--atlas-gold)]">They are derived.</span>
          </h1>
          <p className="mt-6 max-w-xl text-sm leading-relaxed text-white/50">
            Every move below is a double-entry transaction in integer sub-units. Your
            Pouch balance is the sum of its credits minus its debits — nothing else.
            Force a replay and the whole World must reconcile to zero.
          </p>
        </motion.div>

        {data && (
          <>
            <div className="mono mt-12 grid gap-4 sm:grid-cols-2">
              <div className="glass rounded-xl p-6">
                <div className="tracked mb-2 text-[9px] text-white/40">Pouch</div>
                <div className="text-2xl text-[var(--stage-gold)]" data-testid="ledger-pouch">
                  {fmtAxv(data.pouch_sub)}
                </div>
                <div className="mt-1 text-[9px] text-white/30">{data.pouch_sub} sub-units</div>
              </div>
              <div className="glass rounded-xl p-6">
                <div className="tracked mb-2 text-[9px] text-white/40">Staked on yourself</div>
                <div className="text-2xl text-[var(--arcade-cyan)]" data-testid="ledger-stake">
                  {fmtAxv(data.stake_sub)}
                </div>
                <div className="mt-1 text-[9px] text-white/30">{data.stake_sub} sub-units</div>
              </div>
            </div>

            <div className="mt-10 space-y-3" data-testid="ledger-transactions">
              {data.transactions.map((t, i) => (
                <motion.div
                  key={t.tx_id}
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: Math.min(i, 6) * 0.05 }}
                  className="glass rounded-xl p-5"
                  data-testid="ledger-transaction"
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-3">
                    <div className="text-[13px] text-white/70">{KIND_LABEL[t.kind] || t.kind}</div>
                    <div
                      className={`mono text-[15px] ${
                        t.pouch_delta_sub < 0 ? "text-[var(--stage-ember)]" : "text-emerald-400/85"
                      }`}
                    >
                      {t.pouch_delta_sub < 0 ? "−" : "+"}
                      {fmtAxv(Math.abs(t.pouch_delta_sub))}
                    </div>
                  </div>
                  <div className="mono mt-2 break-all text-[9px] text-white/25">{t.tx_id}</div>
                  {t.fee_lines?.length > 0 && (
                    <div className="mono mt-3 space-y-1 border-t border-white/8 pt-3 text-[10px] text-white/35">
                      {t.fee_lines.map((f) => (
                        <div key={f.label} className="flex justify-between">
                          <span>{f.label}</span>
                          <span>{fmtAxv(f.amount_sub)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>

            <div className="mt-12">
              <button type="button" data-testid="reconcile-button" onClick={replay} className="btn">
                Force a full replay
              </button>
              {recon && recon !== "loading" && (
                <motion.div
                  initial={{ opacity: 0, y: 14 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="glass mono mt-6 rounded-xl p-6 text-[11px]"
                  data-testid="reconcile-result"
                >
                  <div
                    className={`mb-4 flex items-center gap-2 text-[12px] ${
                      recon.conserved ? "text-emerald-400" : "text-red-400"
                    }`}
                  >
                    {recon.conserved ? <ShieldCheck size={14} /> : <TriangleAlert size={14} />}
                    <span data-testid="reconcile-verdict">
                      {recon.conserved
                        ? "Conservation holds. Every sub-unit accounted for."
                        : "INVARIANT BROKEN"}
                    </span>
                  </div>
                  <div className="space-y-1 text-white/45">
                    <div className="flex justify-between">
                      <span>transactions replayed</span>
                      <span>{recon.transactions}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>total credits</span>
                      <span>{recon.total_credits_sub}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>total debits</span>
                      <span>{recon.total_debits_sub}</span>
                    </div>
                  </div>
                  <div className="mt-4 space-y-1 border-t border-white/8 pt-4 text-white/35">
                    {Object.entries(recon.accounts).map(([acc, v]) => (
                      <div key={acc} className="flex justify-between gap-6">
                        <span className="truncate">{acc}</span>
                        <span>{fmtAxv(v)}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
